// "New manufacturer request" modal: pick products, flag which inputs you need
// (whole sections, individual part fields, or custom questions), add the
// manufacturer's contact details, send. Also used from the LCA wizard with
// `prefill` carrying the product + flagged questions from the flag basket.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { unwrap } from './SectionEditors';
import { PRODUCT_CERTS } from '../../constants/productCerts';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const FONT = "'Raleway', 'SF Pro Display', sans-serif";

const SECTION_OPTIONS = [
  { key: 'weight', label: 'Product weight' },
  { key: 'country_of_origin', label: 'Country of origin' },
  { key: 'parts', label: 'Parts & materials' },
  { key: 'packaging', label: 'Packaging' },
  { key: 'manufacturing', label: 'Manufacturing & energy' },
  { key: 'transport', label: 'Upstream transport' },
];

const PART_FIELD_OPTIONS = [
  { field: 'process_name', label: 'Process' },
  { field: 'material_category', label: 'Material' },
  { field: 'material_subcategory', label: 'Material type' },
  { field: 'material_quality', label: 'Quality' },
  { field: 'weight_grams', label: 'Weight' },
];

function emptySelection() {
  return { sections: new Set(), partFields: new Set(), certs: new Set(), customs: [] };
}

export default function RequestModal({ supplierId, authHeaders, knownContacts = [], onClose, onCreated, prefill = null }) {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assessments, setAssessments] = useState({}); // productId -> assessment | null (null = loaded, none)
  const [selections, setSelections] = useState(() => {
    if (!prefill?.items) return {};
    const initial = {};
    for (const item of prefill.items) {
      const sel = emptySelection();
      for (const q of item.questions || []) {
        if (q.key === 'part_field') sel.partFields.add(`${q.part_index}:${q.field}`);
        else if (q.key === 'certification') sel.certs.add(q.cert_id);
        else if (q.key === 'custom') sel.customs.push({ label: q.label });
        else sel.sections.add(q.key);
      }
      initial[item.product_id] = sel;
    }
    return initial;
  });
  const [certOptions, setCertOptions] = useState(PRODUCT_CERTS);
  const [contactName, setContactName] = useState(prefill?.manufacturer_name || '');
  const [contactEmail, setContactEmail] = useState(prefill?.manufacturer_email || '');
  const [message, setMessage] = useState('');
  const [customDrafts, setCustomDrafts] = useState({});
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendUpgrade, setSendUpgrade] = useState(false);
  const [result, setResult] = useState(null);

  const selectedIds = useMemo(() => Object.keys(selections), [selections]);

  // ---- Load products ----
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await axios.get(`${API_BASE_URL}/products`, {
          params: { supplier_id: supplierId, items_per_page: 200, page: 1 },
          headers,
        });
        if (active) setProducts(unwrap(res.data));
      } catch (e) {
        console.error('Failed to load products', e);
      } finally {
        if (active) setProductsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [supplierId, authHeaders]);

  // ---- Certification catalogue ----
  // Preferred from the server, because this is the one surface where a cert id
  // the server doesn't know becomes a validation error on send. The bundled
  // list is the fallback so the section still works if the fetch fails.
  useEffect(() => {
    let active = true;
    axios
      .get(`${API_BASE_URL}/product-certifications`)
      .then((res) => {
        const list = unwrap(res.data);
        if (active && Array.isArray(list) && list.length > 0) setCertOptions(list);
      })
      .catch((e) => console.error('Failed to load certifications, using bundled list', e));
    return () => { active = false; };
  }, []);

  // ---- Lazy assessment per selected product (powers part flags + presets) ----
  const ensureAssessment = useCallback(async (productId) => {
    setAssessments((prev) => {
      if (productId in prev) return prev;
      (async () => {
        try {
          const headers = await authHeaders();
          const res = await axios.get(`${API_BASE_URL}/lca/assessments`, {
            params: { product_id: productId, supplier_id: supplierId },
            headers,
          });
          setAssessments((p) => ({ ...p, [productId]: res.data?.data || null }));
        } catch {
          setAssessments((p) => ({ ...p, [productId]: null }));
        }
      })();
      return { ...prev, [productId]: undefined };
    });
  }, [authHeaders, supplierId]);

  useEffect(() => {
    selectedIds.forEach((pid) => ensureAssessment(pid));
  }, [selectedIds, ensureAssessment]);

  // ---- Selection mutations ----
  const toggleProduct = (productId) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (next[productId]) delete next[productId];
      else next[productId] = emptySelection();
      return next;
    });
  };

  const mutateSelection = (productId, fn) => {
    setSelections((prev) => {
      const cur = prev[productId] || emptySelection();
      const next = {
        sections: new Set(cur.sections),
        partFields: new Set(cur.partFields),
        certs: new Set(cur.certs),
        customs: [...cur.customs],
      };
      fn(next);
      return { ...prev, [productId]: next };
    });
  };

  const toggleSection = (productId, key) =>
    mutateSelection(productId, (sel) => {
      if (sel.sections.has(key)) sel.sections.delete(key);
      else {
        sel.sections.add(key);
        // Whole-parts supersedes individual part-field flags.
        if (key === 'parts') sel.partFields.clear();
      }
    });

  const togglePartField = (productId, partIndex, field) =>
    mutateSelection(productId, (sel) => {
      const k = `${partIndex}:${field}`;
      if (sel.partFields.has(k)) sel.partFields.delete(k);
      else {
        sel.partFields.add(k);
        sel.sections.delete('parts');
      }
    });

  const toggleCert = (productId, certId) =>
    mutateSelection(productId, (sel) => {
      if (sel.certs.has(certId)) sel.certs.delete(certId);
      else sel.certs.add(certId);
    });

  const addCustom = (productId) => {
    const label = String(customDrafts[productId] || '').trim();
    if (!label) return;
    mutateSelection(productId, (sel) => sel.customs.push({ label }));
    setCustomDrafts((d) => ({ ...d, [productId]: '' }));
  };

  const selectMissing = (productId) => {
    const product = products.find((p) => p.id === productId) || {};
    const assessment = assessments[productId];
    mutateSelection(productId, (sel) => {
      if (!(product.weight_grams || assessment?.weight_grams)) sel.sections.add('weight');
      if (!product.country_of_origin) sel.sections.add('country_of_origin');
      const parts = assessment?.parts;
      if (!Array.isArray(parts) || parts.length === 0) {
        sel.sections.add('parts');
        sel.partFields.clear();
      } else if (!sel.sections.has('parts')) {
        parts.forEach((part, i) => {
          if (!part.material_category) sel.partFields.add(`${i}:material_category`);
          else if (!part.material_subcategory) sel.partFields.add(`${i}:material_subcategory`);
          if (!part.process_name) sel.partFields.add(`${i}:process_name`);
          if (!part.weight_grams) sel.partFields.add(`${i}:weight_grams`);
        });
      }
      if (!assessment?.packaging_materials?.length) sel.sections.add('packaging');
      if (!assessment?.assembly_location) sel.sections.add('manufacturing');
      if (!assessment?.transport_legs?.length) sel.sections.add('transport');
    });
  };

  const questionCount = (sel) => sel.sections.size + sel.partFields.size + sel.certs.size + sel.customs.length;
  const totalQuestions = selectedIds.reduce((sum, pid) => sum + questionCount(selections[pid] || emptySelection()), 0);
  const productsWithoutQuestions = selectedIds.filter((pid) => questionCount(selections[pid]) === 0);

  const canSend =
    selectedIds.length > 0 &&
    productsWithoutQuestions.length === 0 &&
    /\S+@\S+\.\S+/.test(contactEmail) &&
    !sending;

  // ---- Send ----
  const send = async () => {
    setSending(true);
    setSendError(null);
    setSendUpgrade(false);
    try {
      const items = selectedIds.map((pid) => {
        const sel = selections[pid];
        return {
          product_id: pid,
          questions: [
            ...[...sel.sections].map((key) => ({ key })),
            ...[...sel.partFields].map((k) => {
              const [idx, field] = k.split(':');
              return { key: 'part_field', part_index: Number(idx), field };
            }),
            ...[...sel.certs].map((certId) => ({ key: 'certification', cert_id: certId })),
            ...sel.customs.map((c) => ({ key: 'custom', label: c.label })),
          ],
        };
      });
      const headers = await authHeaders();
      const res = await axios.post(
        `${API_BASE_URL}/manufacturer-requests`,
        { manufacturer_name: contactName, manufacturer_email: contactEmail, message, items },
        { headers }
      );
      setResult(res.data?.data);
      onCreated?.();
    } catch (e) {
      setSendError(e?.response?.data?.error || e.message || 'Failed to send request');
      // Free/Starter/Team suppliers can compose a request (the feature is
      // visible so they can see what they'd get) but sending is Pro+. Route
      // them to billing instead of leaving a bare error.
      setSendUpgrade(e?.response?.data?.code === 'MANUFACTURER_REQUESTS_NOT_AVAILABLE');
    } finally {
      setSending(false);
    }
  };

  const visibleProducts = products.filter(
    (p) => !search || `${p.name || ''} ${p.code || ''}`.toLowerCase().includes(search.toLowerCase())
  );

  // ---- Result screen ----
  if (result) {
    return (
      <ModalShell onClose={onClose} title="Request sent">
        <div className="text-center py-2">
          <div style={{ fontSize: 40, marginBottom: 6 }}>{result.email_sent ? '📨' : '🔗'}</div>
          <h5 className="fw-bold" style={{ color: '#111827' }}>
            {result.email_sent
              ? `Emailed to ${result.manufacturer_email}`
              : 'Request created — share the link yourself'}
          </h5>
          <p style={{ color: '#6b7280', fontSize: 13, maxWidth: 420, margin: '0 auto 16px' }}>
            {result.email_sent
              ? 'They\'ll open the link, enter the access code, and answer only the questions you flagged.'
              : 'Email isn\'t configured on this environment, so send them the link and code below.'}
          </p>
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, textAlign: 'left', fontSize: 13 }}>
            <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
              <span style={{ wordBreak: 'break-all', color: '#374151' }}>{result.portal_url}</span>
              <CopyBtn text={result.portal_url} label="Copy link" />
            </div>
            <div className="d-flex justify-content-between align-items-center gap-2">
              <span style={{ color: '#374151' }}>
                Access code: <strong style={{ letterSpacing: 3, fontFamily: 'SF Mono, Menlo, monospace' }}>{result.access_code}</strong>
              </span>
              <CopyBtn text={result.access_code} label="Copy code" />
            </div>
          </div>
          <button className="btn mt-3" onClick={onClose} style={{ background: '#111827', color: '#fff', borderRadius: 8, fontWeight: 600, padding: '8px 28px' }}>
            Done
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onClose} title="New manufacturer request" wide>
      {/* 1. Products */}
      <StepLabel n={1} text="Which products?" />
      <input
        type="text"
        className="form-control form-control-sm mb-2"
        placeholder="Search products…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ borderRadius: 8, maxWidth: 340 }}
      />
      <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }}>
        {productsLoading && <div className="p-3 text-center" style={{ color: '#9ca3af', fontSize: 13 }}>Loading products…</div>}
        {!productsLoading && visibleProducts.length === 0 && (
          <div className="p-3 text-center" style={{ color: '#9ca3af', fontSize: 13 }}>No products found.</div>
        )}
        {visibleProducts.map((p) => (
          <label
            key={p.id}
            className="d-flex align-items-center gap-2"
            style={{ padding: '8px 12px', borderBottom: '1px solid #f9fafb', cursor: 'pointer', margin: 0 }}
          >
            <input type="checkbox" checked={!!selections[p.id]} onChange={() => toggleProduct(p.id)} />
            <span style={{ fontSize: 13, color: '#111827', fontWeight: 500 }}>{p.name}</span>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>{p.code || ''}</span>
          </label>
        ))}
      </div>

      {/* 2. Questions per product */}
      {selectedIds.length > 0 && (
        <>
          <StepLabel n={2} text="What do you need to know?" />
          <div className="d-flex flex-column gap-2">
            {selectedIds.map((pid) => {
              const product = products.find((p) => p.id === pid) || { name: prefill?.product_names?.[pid] || 'Product' };
              const sel = selections[pid];
              const assessment = assessments[pid];
              const parts = Array.isArray(assessment?.parts) ? assessment.parts : [];
              return (
                <div key={pid} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
                      {product.name}
                      <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
                        {questionCount(sel)} question{questionCount(sel) === 1 ? '' : 's'}
                      </span>
                    </div>
                    <button
                      className="btn btn-sm"
                      onClick={() => selectMissing(pid)}
                      disabled={assessment === undefined}
                      style={{ background: '#eef2ff', color: '#4338ca', borderRadius: 7, fontSize: 12, fontWeight: 600, border: 'none' }}
                    >
                      ✨ Select what's missing
                    </button>
                  </div>

                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {SECTION_OPTIONS.map((opt) => (
                      <Chip
                        key={opt.key}
                        active={sel.sections.has(opt.key)}
                        onClick={() => toggleSection(pid, opt.key)}
                      >
                        {opt.label}
                      </Chip>
                    ))}
                  </div>

                  {parts.length > 0 && !sel.sections.has('parts') && (
                    <div style={{ background: '#fafbfc', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                        Ask about specific parts
                      </div>
                      {parts.map((part, i) => (
                        <div key={i} className="d-flex align-items-center flex-wrap gap-1" style={{ marginBottom: 4 }}>
                          <span style={{ fontSize: 12, color: '#374151', minWidth: 130 }}>
                            {part.name || part.material_subcategory || part.material_category || `Part ${i + 1}`}
                            {part.weight_grams ? ` · ${part.weight_grams}g` : ''}
                          </span>
                          {PART_FIELD_OPTIONS.map((f) => (
                            <Chip
                              key={f.field}
                              small
                              active={sel.partFields.has(`${i}:${f.field}`)}
                              onClick={() => togglePartField(pid, i, f.field)}
                            >
                              {f.label}
                            </Chip>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}

                  {certOptions.length > 0 && (
                    <div style={{ background: '#fafbfc', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                        Ask for certificates
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {certOptions.map((cert) => {
                          const onFile = hasCertOnFile(assessment, cert.id);
                          return (
                            <Chip
                              key={cert.id}
                              small
                              active={sel.certs.has(cert.id)}
                              onClick={() => toggleCert(pid, cert.id)}
                              title={onFile ? 'Already on file — asking again replaces the current document' : undefined}
                            >
                              {cert.label}{onFile ? ' · on file' : ''}
                            </Chip>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                        They'll be asked to upload a document for each one you tick.
                      </div>
                    </div>
                  )}

                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Add a custom question…"
                      value={customDrafts[pid] || ''}
                      onChange={(e) => setCustomDrafts((d) => ({ ...d, [pid]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(pid); } }}
                      style={{ borderRadius: 8, maxWidth: 380, fontSize: 13 }}
                    />
                    <button className="btn btn-sm" onClick={() => addCustom(pid)} style={{ border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12 }}>
                      Add
                    </button>
                  </div>
                  {sel.customs.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {sel.customs.map((c, i) => (
                        <span key={i} style={{ background: '#f3f4f6', borderRadius: 99, padding: '3px 10px', fontSize: 12, color: '#374151' }}>
                          “{c.label}”
                          <button
                            onClick={() => mutateSelection(pid, (s) => s.customs.splice(i, 1))}
                            style={{ border: 'none', background: 'transparent', color: '#9ca3af', marginLeft: 4, padding: 0 }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* 3. Contact */}
      {selectedIds.length > 0 && (
        <>
          <StepLabel n={3} text="Who's the manufacturer?" />
          <div className="row g-2 mb-2">
            <div className="col-md-5">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Contact name (optional)"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>
            <div className="col-md-7">
              <input
                type="email"
                className="form-control form-control-sm"
                placeholder="Email address *"
                list="mfr-known-contacts"
                value={contactEmail}
                onChange={(e) => {
                  setContactEmail(e.target.value);
                  const known = knownContacts.find((c) => c.email === e.target.value);
                  if (known?.name && !contactName) setContactName(known.name);
                }}
                style={{ borderRadius: 8 }}
              />
              <datalist id="mfr-known-contacts">
                {knownContacts.map((c) => (
                  <option key={c.email} value={c.email}>{c.name}</option>
                ))}
              </datalist>
            </div>
          </div>
          <textarea
            className="form-control form-control-sm"
            rows={2}
            placeholder="Optional message — context helps them answer faster (e.g. which order/season these are from)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={{ borderRadius: 8, fontSize: 13 }}
          />
        </>
      )}

      {/* Footer */}
      <div className="d-flex align-items-center justify-content-between mt-4">
        <div style={{ fontSize: 12, color: productsWithoutQuestions.length > 0 ? '#b91c1c' : '#6b7280' }}>
          {productsWithoutQuestions.length > 0
            ? 'Every selected product needs at least one question.'
            : selectedIds.length > 0
              ? `${totalQuestions} question${totalQuestions === 1 ? '' : 's'} across ${selectedIds.length} product${selectedIds.length === 1 ? '' : 's'}`
              : 'Select at least one product.'}
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: 8 }} onClick={onClose}>Cancel</button>
          <button
            className="btn btn-sm"
            disabled={!canSend}
            onClick={send}
            style={{ background: '#111827', color: '#fff', borderRadius: 8, fontWeight: 600, padding: '6px 22px', opacity: canSend ? 1 : 0.55 }}
          >
            {sending ? 'Sending…' : 'Send request'}
          </button>
        </div>
      </div>
      {sendError && (
        <div className="mt-2" style={{ color: '#b91c1c', fontSize: 13 }}>
          {sendError}
          {sendUpgrade && (
            <>
              {' '}
              <a href="/billing" style={{ color: '#b91c1c', fontWeight: 600 }}>See plans</a>
            </>
          )}
        </div>
      )}
    </ModalShell>
  );
}

// ---------- Pieces ----------

function ModalShell({ children, onClose, title, wide }) {
  return (
    <div
      className="modal d-block"
      style={{ background: 'rgba(17,24,39,0.5)', fontFamily: FONT, overflowY: 'auto' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`modal-dialog ${wide ? 'modal-lg' : ''} modal-dialog-centered`}>
        <div className="modal-content" style={{ borderRadius: 14 }}>
          <div className="modal-header" style={{ borderBottom: '1px solid #f3f4f6' }}>
            <h5 className="modal-title fw-bold" style={{ color: '#111827', fontSize: 17 }}>{title}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body" style={{ padding: '16px 20px 20px' }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

function StepLabel({ n, text }) {
  return (
    <div className="d-flex align-items-center gap-2" style={{ margin: '16px 0 8px' }}>
      <span style={{ background: '#111827', color: '#fff', width: 20, height: 20, borderRadius: 99, fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{n}</span>
      <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{text}</span>
    </div>
  );
}

// A cert only counts as held once it has a proof document — same rule the
// results page and PDF apply when deciding whether to render the badge.
function hasCertOnFile(assessment, certId) {
  const raw = assessment?.social_impact;
  if (!raw) return false;
  const si = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
  return Array.isArray(si?.product) && si.product.includes(certId) && !!si?.certFiles?.[certId];
}

function Chip({ children, active, onClick, small, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: active ? '#111827' : '#fff',
        color: active ? '#fff' : '#374151',
        border: `1px solid ${active ? '#111827' : '#e5e7eb'}`,
        borderRadius: 99,
        padding: small ? '2px 9px' : '4px 12px',
        fontSize: small ? 11 : 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {active ? '✓ ' : ''}{children}
    </button>
  );
}

function CopyBtn({ text, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="btn btn-sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch { /* noop */ }
      }}
      style={{ border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12, whiteSpace: 'nowrap', background: copied ? '#ecfdf5' : '#fff', color: copied ? '#047857' : '#374151' }}
    >
      {copied ? '✓ Copied' : label}
    </button>
  );
}
